import React, { useEffect, useState } from 'react';

import { useLocalization } from '@progress/kendo-react-intl';
import { useHistory, useLocation } from "react-router-dom";
import { Form, Field, FormElement } from '@progress/kendo-react-form';
import { Button } from '@progress/kendo-react-buttons';
import { emailValidator } from '../validators';
// @ts-ignore
import PrivacyBanner from 'react-ford-ccpa-banner';
import logo from "../assets/images/Ford_BuildingTogether_Blue_Screen_RGB_Vt_v2.png"
import sideImage from "../assets/images/2025_D4URC_Dealer_Portal_Forgot_Password_Page_66836.png";
import '../styles/_login.scss';
import { useServices } from 'src/services';
import { Input } from 'src/components/form/Input';
import { AppContext } from '../AppContext';

const ForgotUsernameScreen = () => {
    const { ...formValues } = React.useContext(AppContext);
    const localizationService = useLocalization();
    const history = useHistory();
    const location = useLocation<any>();
    const { language } = localizationService;
    const { d4ur } = useServices();
    const [maskedEmail, setMaskedEmail] = useState<string>('');
    const [actualEmail, setActualEmail] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string>('');
    const [verificationError, setVerificationError] = useState<string>('');
    const [storedPartsCode, setStoredPartsCode] = useState<string>('');

    const maskEmail = (email: string): string => {
        if (!email) return '';
        
        const [localPart, domain] = email.split('@');
        if (!domain) return email;
        
        // Get first 3 letters of local part
        const firstThree = localPart.length >= 3 ? localPart.substring(0, 3) : localPart;
        
        // Get domain without TLD (everything before the last dot)
        const lastDotIndex = domain.lastIndexOf('.');
        const domainWithoutTld = lastDotIndex > 0 ? domain.substring(0, lastDotIndex) : domain;
        const tld = lastDotIndex > 0 ? domain.substring(lastDotIndex) : '';
        
        // Get last 3 letters of domain before TLD
        const lastThree = domainWithoutTld.length >= 3 
            ? domainWithoutTld.substring(domainWithoutTld.length - 3) 
            : domainWithoutTld;
        
        // Create masked local part (first 3 + asterisks for the rest)
        const maskedLocal = firstThree + '*'.repeat(Math.max(0, localPart.length - firstThree.length));
        
        // Create masked domain (asterisks + last 3 + TLD)
        const asteriskCount = Math.max(0, domainWithoutTld.length - 3);
        const maskedDomain = '*'.repeat(asteriskCount) + lastThree + tld;
        
        return `${maskedLocal}@${maskedDomain}`;
    };

    useEffect(() => {
        // Get partsCode from location state or use stored value
        const partsCode = location.state?.partsCode || storedPartsCode;
        
        if (!partsCode) {
            setError('No sales code provided');
            setLoading(false);
            return;
        }

        // Store partsCode in state so it persists across navigation
        if (partsCode && partsCode !== storedPartsCode) {
            setStoredPartsCode(partsCode);
        }

        const fetchDealerContact = async () => {
            try {
                setLoading(true);
                const response: any = await d4ur.getDealerContactByPartsCode(partsCode);
                console.log('Dealer contact response:', response);
                if (response.result && response.result.contact_info) {
                    const dealerContact = response.result.contact_info;
                    console.log('Dealer contact:', dealerContact);
                    const email = dealerContact.contact_email;
                    if (email) {
                        setActualEmail(email);
                        setMaskedEmail(maskEmail(email));
                    } else {
                        setError('No email found for this sales code');
                    }
                } else {
                    setError('Unable to retrieve dealer contact information');
                }
            } catch (err) {
                console.error('[ForgotUsernameScreen]', 'Error fetching dealer contact:', err);
                setError('Error retrieving dealer contact information');
            } finally {
                setLoading(false);
            }
        };

        fetchDealerContact();
    }, [location.state, storedPartsCode, d4ur]);

    const onVerifyEmail = React.useCallback(
        async (dataItem) => {
            const enteredEmail = dataItem.verification_email?.toLowerCase().trim();
            const storedEmail = actualEmail.toLowerCase().trim();
            
            if (enteredEmail === storedEmail) {
                console.log('success');
                setVerificationError('');
                
                // Generate token for updating contact email
                const partsCode = location.state?.partsCode || storedPartsCode;
                try {
                    const response: any = await d4ur.generateUpdateContactEmailToken(partsCode, actualEmail);
                    console.log('[ForgotUsernameScreen]', 'Token generation response:', response);
                    
                    if (response.result && response.result.result && response.result.result.token) {
                        // Navigate to update contact email page with token
                        history.push(`/update-contact-email/${response.result.result.token}`);
                    } else {
                        console.error('[ForgotUsernameScreen]', 'Token not found in response. Full response:', response);
                        setError('Failed to generate access token. Please try again.');
                    }
                } catch (err) {
                    console.error('[ForgotUsernameScreen]', 'Error generating token:', err);
                    setError('Failed to generate access token. Please try again.');
                }
            } else {
                console.log('failure');
                setVerificationError('Email does not match. Please reach out to PHQ at hello@driveforyourcommunity.com to reset your primary dealer contact email');
            }
        },
        [actualEmail, history, location.state, storedPartsCode, d4ur]
    );

    return (
        <div id="ForgotUsername" className="forgot_pass">
            <div className='main'>
                <div className="left_content">
                    <img 
                        src={logo} 
                        alt={'Ford Drive 4 UR School Logo'} 
                        className='logo' 
                        onClick={() => {
                            history.push("/");
                        }}
                    />
                    <div className="form-container" style={{ maxWidth: 700 }}>
                        <h1 className="form_title">Forgot Username</h1>
                        
                        {loading ? (
                            <p>Loading...</p>
                        ) : error ? (
                            <p style={{ color: 'red' }}>{error}</p>
                        ) : (
                            <>
                                <p style={{ marginTop: '20px', fontSize: '16px' }}>
                                    The existing primary dealer contact for this account is <strong>{maskedEmail}</strong>
                                </p>
                                <div style={{ marginTop: '30px' }}>
                                    <p style={{ fontSize: '14px' }}>
                                        If this is you, click here to{' '}
                                        <a 
                                            href="/login" 
                                            onClick={(e) => {
                                                e.preventDefault();
                                                const partsCode = location.state?.partsCode || storedPartsCode;
                                                history.push("/login", { skipSalesCode: true, partsCode });
                                            }}
                                            style={{ color: '#0066CC', textDecoration: 'underline', cursor: 'pointer' }}
                                        >
                                            Login
                                        </a>
                                        {' '}or{' '}
                                        <a 
                                            href="/password" 
                                            onClick={(e) => {
                                                e.preventDefault();
                                                history.push("/password");
                                            }}
                                            style={{ color: '#0066CC', textDecoration: 'underline', cursor: 'pointer' }}
                                        >
                                            Reset Your Password
                                        </a>
                                    </p>
                                </div>
                                
                                <div style={{ marginTop: '30px' }}>
                                    <p style={{ marginBottom: '15px', fontSize: '14px' }}>
                                        If you need to update the Primary Dealer Contact for the 2026 season, first verify your authority by entering the full email address of the 2025 Primary Dealer Contact shown above.
                                    </p>
                                    <Form
                                        onSubmit={onVerifyEmail}
                                        initialValues={{
                                            ...formValues,
                                            verification_email: ''
                                        }}
                                        render={(formRenderProps) => (
                                            <FormElement horizontal={true} style={{ maxWidth: 700 }}>
                                                {verificationError && (
                                                    <p style={{ color: 'red', marginBottom: '15px', fontSize: '14px' }}>
                                                        {verificationError.split('hello@driveforyourcommunity.com').map((part, index, array) => 
                                                            index === array.length - 1 ? (
                                                                <span key={index}>{part}</span>
                                                            ) : (
                                                                <span key={index}>
                                                                    {part}
                                                                    <a 
                                                                        href="mailto:hello@driveforyourcommunity.com" 
                                                                        style={{ 
                                                                            color: '#0066CC', 
                                                                            textDecoration: 'underline',
                                                                            fontSize: '14px',
                                                                            cursor: 'pointer'
                                                                        }}
                                                                    >
                                                                        hello@driveforyourcommunity.com
                                                                    </a>
                                                                </span>
                                                            )
                                                        )}
                                                    </p>
                                                )}
                                                <Field
                                                    id={'verification_email'}
                                                    name={'verification_email'}
                                                    type={'email'}
                                                    placeholder={'e.g.: peter@gmail.com'}
                                                    label={'Email Address'}
                                                    validator={emailValidator}
                                                    required={true}
                                                    component={Input}
                                                />
                                                <div className='log_in_button'>
                                                    <Button
                                                        className={'log_in_btn'}
                                                        primary={true}
                                                        type={'submit'}
                                                        disabled={!formRenderProps.allowSubmit}
                                                    >
                                                        Verify
                                                    </Button>
                                                </div>
                                            </FormElement>
                                        )}
                                    />
                                </div>
                            </>
                        )}
                    </div>
                </div>
                <div className='right_content'>
                    <img src={sideImage} alt={'Two volunteers talking next to a Ford vehicle'} className='right_img' />        
                </div>
            </div>
            <PrivacyBanner
                containerStyle={{ padding: '10px 0 0' }}
                privacyContainerStyle={{ padding: '10px 0', width: "60%", marginLeft: '20%' }}
                showPrivacy={true}
                language={language}
                team={'ford'}
                imgStyle={{ paddingLeft: '30px' }}
                otherLinks={[
                    { text: "CONTACT US", url: "/contact", target: '_blank' },
                    { text: "PROGRAM TERMS AND CONDITIONS", url: "/terms-eligibility", target: '_blank'},
                    { text: "PROGRAM RESOURCES ", url: "/resources", target: '_blank' },
                ]}
            />
        </div>
    );
}

export default ForgotUsernameScreen;